import React, { useState, useEffect } from 'react';
import { EventCard } from './EventCard';
import { EventGridSkeleton } from './EventCardSkeleton';
import { EnhancedHero } from './EnhancedHero';
import { Button } from './ui/button';
import { RefreshCw, WifiOff } from 'lucide-react';
import { apiService, ApiService, UpcomingEvent } from '../services/api';
import { SemanaCafeSection } from './SemanaCafeSection';
import { useScrollReveal } from '../lib/useScrollReveal';
import {
  resolveFeaturedEventIds,
  splitFeaturedEvents,
} from '../config/featuredEvents';
import { sortCatalogEvents } from '../lib/catalogRelevance';

export function EventGrid() {
  const headingReveal = useScrollReveal<HTMLDivElement>();
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  // Load events from API - NO FALLBACK
  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🎭 [EVENT_GRID] Loading events...');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Tempo limite excedido')), 8000)
      );
      
      const apiPromise = apiService.getUpcomingEventsShared();
      
      const response = await Promise.race([apiPromise, timeoutPromise]);
      
      
      if (response.events && Array.isArray(response.events) && response.events.length > 0) {
        console.log(`🎭 [EVENT_GRID] Loaded ${response.events.length} events from API`);
        setEvents(response.events);
        setError(null);
      } else {
        console.log('🎭 [EVENT_GRID] API returned empty or invalid data');
        setError('Nenhum evento encontrado no momento');
        setEvents([]);
      }
      
    } catch (err) {
      console.log('🎭 [EVENT_GRID] Error loading events:', err.message);
      const errorMessage = err.message === 'Tempo limite excedido' 
        ? 'Conexão muito lenta. Verifique sua internet e tente novamente.'
        : 'Erro ao conectar com o servidor. Tente novamente em alguns instantes.';
      
      setError(errorMessage);
      setEvents([]);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  // Retry function
  const handleRetry = async () => {
    setIsRetrying(true);
    ApiService.clearUpcomingEventsCache();
    await loadEvents();
  };

  // Loading state
  if (isLoading) {
    return (
      <section className="pt-4 pb-8 lg:pt-6 lg:pb-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-modern">
          <EventGridSkeleton count={9} />
        </div>
      </section>
    );
  }

  // Error state - show friendly error message with retry
  if (error || events.length === 0) {
    return (
      <section className="pt-6 pb-8 lg:pt-8 lg:pb-12 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-modern">
          <div className="text-center max-w-lg mx-auto">
            <div className="card-elegant p-12">
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-8 bg-orange-50 dark:bg-orange-950/20 rounded-3xl border border-orange-200 dark:border-orange-800/30">
                <WifiOff className="w-10 h-10 text-orange-500" />
              </div>
              <h2 className="mb-6 text-orange-600 dark:text-orange-400">
                Não foi possível carregar os eventos
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                {error || 'Ocorreu um problema ao buscar os eventos. Verifique sua conexão com a internet e tente novamente.'}
              </p>
              <div className="space-y-4">
                <Button 
                  onClick={handleRetry}
                  disabled={isRetrying}
                  className="btn-modern btn-primary-modern px-8 py-3 w-full sm:w-auto"
                >
                  {isRetrying ? (
                    <>
                      <RefreshCw className="mr-2 h-5 w-5 animate-spin" />
                      Tentando conectar...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-5 w-5" />
                      Tentar Novamente
                    </>
                  )}
                </Button>
                
                {/* Connection status */}
                <div className="text-xs text-muted-foreground">
                  <div className="flex items-center justify-center space-x-2">
                    <WifiOff className="w-3 h-3" />
                    <span>Verifique sua conexão e tente novamente</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // A curadoria vem do backend quando `evento_destaque_peso` existir; ate la,
  // da lista de ids. A agenda abaixo mantém todos os eventos, inclusive os destaques.
  const featuredIds = resolveFeaturedEventIds(events, import.meta.env);
  const { featured } = splitFeaturedEvents(events, featuredIds);
  // So a grade segue a relevancia do backend; hero e vitrine usam o payload original.
  const catalogEvents = sortCatalogEvents(events);

  return (
    <>
      <div className="home-secondary-highlights"><EnhancedHero embedded /></div>
      {featured.length > 0 && <SemanaCafeSection events={featured} />}
      <section id="todos-os-eventos" aria-labelledby="all-events-title" className="home-catalog">
        <div className="container-modern">
          <div {...headingReveal} className="catalog-heading">
            <h2 id="all-events-title">Todos os eventos</h2>
            <span>{events.length} {events.length === 1 ? 'evento' : 'eventos'}</span>
          </div>
          <div className="home-catalog__grid">
            {catalogEvents.map((event, index) => (
              <EventCard key={`${event.eventId}-${index}`} event={event}
                imageLoading={index < 2 ? 'eager' : 'lazy'} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
